import type { Organisation } from '@/api/model/Organisation';
import type { Permission } from '@/constants/permissions';
import { useAuthStore } from '@/stores/authStore';
import { useOrganisationStore } from '@/stores/organisationStore';

/**
 * Whether the signed-in user holds a permission in a given organisation.
 *
 * Permissions are per-organisation -- the same person can be an admin in one
 * and read-only in another -- so the organisation is always explicit here. The
 * router guard needs this form because it must ask about the organisation in
 * the route it is navigating *to*: on a cold load into a deep link the store
 * still holds whatever organisation was last in localStorage.
 *
 * Everything fails closed. An unknown organisation, an unloaded profile and a
 * missing permission list all deny, so the worst case is a control that appears
 * a moment late rather than one that appears and is then taken away.
 *
 * These are plain functions rather than store getters on purpose. `authStore`
 * imports `menuStore`, so putting them on `authStore` and having
 * `menuStore.visibleMenu` call back into it would close an import cycle.
 * Resolving the stores lazily inside the body avoids that, works outside a
 * component (the router guard), and still tracks reactivity when called during
 * render.
 */
export function canFor(organisationPublicId: string, permission: Permission): boolean {
    const profile = useAuthStore().auth.userProfile;

    if (profile === null) {
        return false;
    }

    // Mirrors Gate::before() in AuthServiceProvider, which bypasses every gate
    // for super admins. Without it they would see controls hidden that the
    // server would have allowed.
    if (profile.is_super_admin) {
        return true;
    }

    const organisation: Organisation | undefined =
        useOrganisationStore().findOrganisationByPublicId(organisationPublicId);

    return organisation?.hasPermission(permission) ?? false;
}

/**
 * Whether the signed-in user holds a permission in the organisation the screen
 * is showing.
 *
 * Deliberately the store's organisation, which is the one every query and
 * mutation on the page is scoped to. Gating against anything else makes the
 * controls disagree with what they act on: a Create button enabled because the
 * URL's organisation allows it would still post to the store's, and come back
 * 403 -- or be hidden while the request it would have sent was permitted.
 *
 * The store and the URL can currently disagree on a cold load into another
 * organisation's page; that is issue #9067, fixed separately, after which this
 * distinction stops existing. `canFor` keeps taking an explicit organisation
 * because the route guard has to authorise the one being navigated *to*, which
 * is a different question from what the rendered page may do.
 */
export function can(permission: Permission): boolean {
    return canFor(useOrganisationStore().current_organisation.public_id, permission);
}

export function usePermissions() {
    return { can, canFor };
}
